import { useState, type FormEvent } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useAnnee } from '../hooks/useAnnee';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import {
  Badge,
  Champ,
  Chargement,
  EnTetePage,
  Erreur,
  Infobulle,
  IconeCrayon,
  Modale,
} from '../components/Elements';
import { formaterDate } from '../lib/format';
import type { CalendrierAnnee, Periode, Seance, TypePeriode } from '../types';

const JOURS = [
  { code: 1, libelle: 'Lundi' },
  { code: 2, libelle: 'Mardi' },
  { code: 3, libelle: 'Mercredi' },
  { code: 4, libelle: 'Jeudi' },
  { code: 5, libelle: 'Vendredi' },
  { code: 6, libelle: 'Samedi' },
];

/** Ordre de l'annee scolaire : septembre d'abord, juin en dernier. */
const MOIS = [
  { num: 9, libelle: 'Sept', annee: 0 },
  { num: 10, libelle: 'Oct', annee: 0 },
  { num: 11, libelle: 'Nov', annee: 0 },
  { num: 12, libelle: 'Déc', annee: 0 },
  { num: 1, libelle: 'Janv', annee: 1 },
  { num: 2, libelle: 'Fév', annee: 1 },
  { num: 3, libelle: 'Mars', annee: 1 },
  { num: 4, libelle: 'Avril', annee: 1 },
  { num: 5, libelle: 'Mai', annee: 1 },
  { num: 6, libelle: 'Juin', annee: 1 },
  { num: 7, libelle: 'Juil', annee: 1 },
  { num: 8, libelle: 'Août', annee: 1 },
];

const jourDe = (date: string): number => {
  const n = new Date(`${date}T00:00:00Z`).getUTCDay();
  return n === 0 ? 7 : n;
};
const moisDe = (date: string): number => Number.parseInt(date.slice(5, 7), 10);
const jourMois = (date: string): string => `${date.slice(8, 10)}/${date.slice(5, 7)}`;

/** Toutes les dates d'un mois donne tombant un jour de semaine donne. */
function datesDuCroisement(anneeCivile: number, mois: number, jour: number): string[] {
  const dates: string[] = [];
  const courant = new Date(Date.UTC(anneeCivile, mois - 1, 1));
  while (courant.getUTCMonth() === mois - 1) {
    const n = courant.getUTCDay();
    if ((n === 0 ? 7 : n) === jour) dates.push(courant.toISOString().slice(0, 10));
    courant.setUTCDate(courant.getUTCDate() + 1);
  }
  return dates;
}

/**
 * Calendrier des séances — les dates de cours de l'année.
 *
 * Une année vaut une trentaine de séances par jour ouvré, pas dix mois.
 * Ces dates ne se déduisent pas d'une règle : sur 2025-26, quatre trous
 * ne s'expliquent ni par les vacances ni par les fériés — le samedi avant
 * Pâques et le pont de l'Ascension. Ce sont des décisions de l'école.
 *
 * L'écran reprend donc la grille de son document — un mois par ligne, un
 * jour par colonne — pour qu'on la relise contre le papier, et l'édition
 * se fait au croisement : tous les mardis de novembre d'un coup.
 */
export function CalendrierPage() {
  const { peut } = useAuth();
  // Plus d'entite ici : le calendrier est COMMUN a toutes les ecoles.
  const annee = useAnnee();
  const [selection, setSelection] = useState<Set<string> | null>(null);
  const [modeEdition, setModeEdition] = useState(false);
  const [cellule, setCellule] = useState<{ mois: number; jour: number } | null>(null);
  const [brouillonCellule, setBrouillonCellule] = useState<Set<string>>(new Set());
  const [generation, setGeneration] = useState(false);
  const [evenement, setEvenement] = useState(false);
  const [periode, setPeriode] = useState(false);
  const [enCours, setEnCours] = useState(false);

  const calendrier = useRessource(
    () =>
      annee.anneeId
        ? api.get<CalendrierAnnee>(`/api/annees/${annee.anneeId}/seances`)
        : Promise.resolve(null),
    // L'entite ne fait plus partie de la cle : le calendrier est COMMUN a
    // toutes les ecoles (2026-08-28), il ne change pas quand on bascule.
    [annee.anneeId],
  );

  if (annee.chargement) return <Chargement />;
  if (!annee.annee) {
    return (
      <>
        <EnTetePage titre="Calendrier des séances" />
        <div className="carte p-6 text-sm text-encre-500">
          Aucune année scolaire sur cette entité.
        </div>
      </>
    );
  }

  const seances = calendrier.donnees?.seances ?? [];
  const cours = seances.filter((s) => s.type === 'cours');
  const evenements = seances.filter((s) => s.type === 'evenement');
  const periodes = calendrier.donnees?.periodes ?? [];

  const enregistrees = new Set(cours.map((s) => s.date_seance));
  const affichees = selection ?? enregistrees;
  const modifie = selection !== null;
  const modifiable = peut('calendrier.gerer') && annee.modifiable;

  // L'annee civile de depart se lit sur les bornes de l'annee scolaire.
  const anneeDepart = Number.parseInt(annee.annee.date_debut.slice(0, 4), 10);
  const anneeCivileDe = (mois: number) =>
    anneeDepart + (MOIS.find((m) => m.num === mois)?.annee ?? 0);

  /**
   * Une date non scolaire est GRISEE, pas interdite : l'ecole peut vouloir
   * un rattrapage un jour ferie, et c'est sa decision.
   */
  const periodeDe = (date: string): Periode | undefined =>
    periodes.find((p) => date >= p.date_debut && date <= p.date_fin);

  const ouvrirCellule = (mois: number, jour: number) => {
    const dates = datesDuCroisement(anneeCivileDe(mois), mois, jour);
    // Le brouillon part de l'ETAT COURANT : a l'initialisation le
    // calendrier est vide, donc tous les tags sont blancs. Ailleurs, il
    // reprend ce qui est deja retenu — sans quoi ouvrir une cellule
    // effacerait son contenu.
    setBrouillonCellule(new Set(dates.filter((d) => affichees.has(d))));
    setCellule({ mois, jour });
  };

  const validerCellule = () => {
    if (!cellule) return;
    const dates = datesDuCroisement(anneeCivileDe(cellule.mois), cellule.mois, cellule.jour);
    const suivante = new Set(affichees);
    for (const d of dates) {
      if (brouillonCellule.has(d)) suivante.add(d);
      else suivante.delete(d);
    }
    setSelection(suivante);
    setCellule(null);
  };

  const enregistrer = async () => {
    setEnCours(true);
    try {
      await api.put(`/api/annees/${annee.anneeId}/seances`, { dates: [...affichees].sort() });
      setSelection(null);
      setModeEdition(false);
      calendrier.recharger();
    } catch (cause) {
      window.alert(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const parJourAffiche = (jour: number) => [...affichees].filter((d) => jourDe(d) === jour).length;

  const moisUtiles = MOIS.filter(
    (m) =>
      modeEdition ||
      [...affichees, ...evenements.map((e) => e.date_seance)].some((d) => moisDe(d) === m.num),
  );

  return (
    <>
      <EnTetePage
        titre="Calendrier des séances"
        description="Une année vaut une trentaine de séances par jour, pas dix mois. Ces dates sont arrêtées chaque année : elles servent au prorata d’une inscription commencée en cours d’année ou interrompue. Ce calendrier est COMMUN à toutes les écoles — le modifier les affecte toutes."
        actions={
          <>
            {modifiable && (
              <>
                <button
                  type="button"
                  className={modeEdition ? 'bouton-principal' : 'bouton-secondaire'}
                  onClick={() => {
                    setModeEdition(!modeEdition);
                    setCellule(null);
                  }}
                >
                  {modeEdition ? 'Quitter l’édition' : 'Modifier le calendrier'}
                </button>
                <button
                  type="button"
                  className="bouton-secondaire"
                  onClick={() => setGeneration(true)}
                >
                  Proposer des dates
                </button>
              </>
            )}
          </>
        }
      />

      {!annee.modifiable && (
        <div className="carte mb-4 border-encre-200 bg-encre-50 p-4 text-sm text-encre-600">
          <strong className="font-medium text-encre-800">
            Année {annee.annee.libelle} clôturée.
          </strong>{' '}
          Son calendrier est figé (RG-27) : il reste consultable, mais ne peut plus être modifié.
        </div>
      )}

      {modeEdition && (
        <div className="carte mb-4 border-encre-200 bg-encre-50 p-4 text-sm text-encre-600">
          Cliquez le <span className="font-semibold">crayon</span> d’une cellule pour ouvrir tous
          les jours du croisement — tous les mardis de novembre, par exemple. Les dates{' '}
          <span className="rounded bg-encre-200 px-1 text-xs text-encre-500">grisées</span> tombent
          en vacances ou un jour férié : elles restent sélectionnables, la décision vous appartient.
        </div>
      )}

      {modifie && (
        <div className="carte mb-4 flex flex-wrap items-center justify-between gap-3 border-amber-200 bg-amber-50 p-4">
          <span className="text-sm text-amber-900">
            Modifications non enregistrées — <strong>{affichees.size}</strong> séance(s) au lieu de{' '}
            {enregistrees.size}.
          </span>
          <span className="flex gap-2">
            <button type="button" className="bouton-secondaire" onClick={() => setSelection(null)}>
              Annuler
            </button>
            <button
              type="button"
              className="bouton-principal"
              disabled={enCours}
              onClick={() => void enregistrer()}
            >
              Enregistrer
            </button>
          </span>
        </div>
      )}

      {(calendrier.donnees?.conflits ?? []).length > 0 && (
        <div className="carte mb-4 border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <strong className="font-medium">
            {calendrier.donnees!.conflits.length} séance(s) en période non scolaire.
          </strong>{' '}
          Ce n’est pas une erreur — un rattrapage est possible — mais vérifiez :{' '}
          {calendrier
            .donnees!.conflits.slice(0, 4)
            .map((c) => `${formaterDate(c.date_seance)} (${c.libelle})`)
            .join(', ')}
          {calendrier.donnees!.conflits.length > 4 && '…'}
        </div>
      )}

      {calendrier.erreur && (
        <Erreur message={calendrier.erreur} onReessayer={calendrier.recharger} />
      )}

      {calendrier.chargement ? (
        <Chargement />
      ) : affichees.size === 0 && !modeEdition ? (
        <div className="carte p-6 text-sm text-encre-500">
          Aucune séance sur {annee.annee.libelle}.{' '}
          {modifiable ? (
            <>
              Passez en{' '}
              <button type="button" className="underline" onClick={() => setModeEdition(true)}>
                mode édition
              </button>{' '}
              pour saisir les dates cellule par cellule, ou{' '}
              <button type="button" className="underline" onClick={() => setGeneration(true)}>
                proposez-les entre deux bornes
              </button>
              .
            </>
          ) : (
            'Un gestionnaire doit le définir.'
          )}
        </div>
      ) : (
        <div className="carte overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-encre-50">
              <tr>
                <th className="w-16 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-encre-500">
                  Mois
                </th>
                {JOURS.map((j) => (
                  <th
                    key={j.code}
                    className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-encre-500"
                  >
                    {j.libelle}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-encre-100">
              {moisUtiles.map((m) => (
                <tr key={m.num}>
                  <th className="bg-encre-50 px-3 py-2 text-left text-xs font-medium text-encre-600">
                    {m.libelle}
                  </th>
                  {JOURS.map((j) => {
                    const ouverte = cellule?.mois === m.num && cellule?.jour === j.code;
                    return (
                      <td
                        key={j.code}
                        className={`relative px-2 py-2 align-top ${
                          ouverte ? 'bg-blue-50 ring-2 ring-inset ring-blue-400' : ''
                        }`}
                      >
                        {ouverte ? (
                          <CelluleEnEdition
                            dates={datesDuCroisement(anneeCivileDe(m.num), m.num, j.code)}
                            selection={brouillonCellule}
                            onBasculer={(date) => {
                              const suivante = new Set(brouillonCellule);
                              if (suivante.has(date)) suivante.delete(date);
                              else suivante.add(date);
                              setBrouillonCellule(suivante);
                            }}
                            periodeDe={periodeDe}
                            onValider={validerCellule}
                            onAnnuler={() => setCellule(null)}
                          />
                        ) : (
                          <CelluleLecture
                            dates={[...affichees].filter(
                              (d) => moisDe(d) === m.num && jourDe(d) === j.code,
                            )}
                            evenements={evenements.filter(
                              (e) => moisDe(e.date_seance) === m.num && jourDe(e.date_seance) === j.code,
                            )}
                            periodeDe={periodeDe}
                            modeEdition={modeEdition}
                            onOuvrir={() => ouvrirCellule(m.num, j.code)}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-encre-300 bg-encre-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold uppercase text-encre-500">
                  Total
                </th>
                {JOURS.map((j) => {
                  const n = parJourAffiche(j.code);
                  return (
                    <td key={j.code} className="px-3 py-2 text-center">
                      <span
                        className={`tabulaire text-sm font-semibold ${
                          n === 0 ? 'text-encre-300' : 'text-encre-900'
                        }`}
                      >
                        {n}
                      </span>
                      <span className="ml-1 text-xs text-encre-400">séances</span>
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <BlocPeriodes
        periodes={periodes}
        modifiable={modifiable}
        onAjouter={() => setPeriode(true)}
        onRetirer={async (id) => {
          await api.delete(`/api/periodes/${id}`);
          calendrier.recharger();
        }}
      />

      <BlocEvenements
        evenements={evenements}
        modifiable={modifiable}
        onAjouter={() => setEvenement(true)}
        onRetirer={async (id) => {
          await api.delete(`/api/seances/${id}`);
          calendrier.recharger();
        }}
      />

      <FormulaireGeneration
        ouverte={generation}
        anneeId={annee.anneeId}
        debutDefaut={annee.annee.date_debut.slice(0, 10)}
        finDefaut={annee.annee.date_fin.slice(0, 10)}
        onFermer={() => setGeneration(false)}
        onGenere={(dates) => {
          setGeneration(false);
          setSelection(new Set([...affichees, ...dates]));
        }}
      />

      <FormulaireEvenement
        ouverte={evenement}
        anneeId={annee.anneeId}
        onFermer={() => setEvenement(false)}
        onEnregistre={() => {
          setEvenement(false);
          calendrier.recharger();
        }}
      />

      <FormulairePeriode
        ouverte={periode}
        anneeId={annee.anneeId}
        onFermer={() => setPeriode(false)}
        onEnregistre={() => {
          setPeriode(false);
          calendrier.recharger();
        }}
      />
    </>
  );
}

/** Cellule au repos : les dates retenues, plus le crayon en mode édition. */
function CelluleLecture({
  dates,
  evenements,
  periodeDe,
  modeEdition,
  onOuvrir,
}: {
  dates: string[];
  evenements: Seance[];
  periodeDe: (date: string) => Periode | undefined;
  modeEdition: boolean;
  onOuvrir: () => void;
}) {
  // Cours et événements fusionnés puis triés : rendus séparément,
  // l'audition du 13 juin s'affichait après les samedis 20 et 27.
  const cases = [
    ...dates.map((d) => ({ date: d, fete: null as Seance | null })),
    ...evenements.map((e) => ({ date: e.date_seance, fete: e })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <>
      {modeEdition && (
        <Infobulle texte="Modifier ce croisement" className="absolute right-1 top-1 inline-flex">
          <button
            type="button"
            onClick={onOuvrir}
            aria-label="Modifier ce croisement"
            className="rounded p-0.5 text-encre-400 hover:bg-encre-100 hover:text-encre-800"
          >
            <IconeCrayon />
          </button>
        </Infobulle>
      )}
      <div className={`flex flex-wrap justify-center gap-1 ${modeEdition ? 'pr-4' : ''}`}>
        {cases.map(({ date, fete }) =>
          fete ? (
            <Infobulle
              key={fete.id}
              texte={`${fete.libelle}${fete.lieu ? ` — ${fete.lieu}` : ''}`}
              className="tabulaire rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800"
            >
              {jourMois(date)} ★
            </Infobulle>
          ) : (
            <Infobulle
              key={date}
              texte={periodeDe(date)?.libelle}
              className={`tabulaire rounded px-1.5 py-0.5 text-xs ${
                periodeDe(date)
                  ? 'bg-encre-200 text-encre-500 line-through'
                  : 'bg-encre-100 text-encre-800'
              }`}
            >
              {jourMois(date)}
            </Infobulle>
          ),
        )}
      </div>
    </>
  );
}

/**
 * Cellule ouverte : TOUS les jours du croisement, en tags actionnables.
 *
 * Bleu = retenu, blanc = écarté. Les dates non scolaires sont grisées mais
 * restent sélectionnables — c'est l'école qui décide, pas le calendrier
 * de l'Éducation nationale.
 */
function CelluleEnEdition({
  dates,
  selection,
  onBasculer,
  periodeDe,
  onValider,
  onAnnuler,
}: {
  dates: string[];
  selection: Set<string>;
  onBasculer: (date: string) => void;
  periodeDe: (date: string) => Periode | undefined;
  onValider: () => void;
  onAnnuler: () => void;
}) {
  return (
    <>
      <div className="absolute right-1 top-1 flex gap-0.5">
        <Infobulle texte="Valider ce croisement" className="inline-flex">
          <button
            type="button"
            onClick={onValider}
            aria-label="Valider ce croisement"
            className="rounded bg-emerald-600 p-0.5 text-white hover:bg-emerald-700"
          >
            <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="currentColor" aria-hidden>
              <path d="M6.2 12.3 2.5 8.6l1.4-1.4 2.3 2.3 5.9-5.9 1.4 1.4-7.3 7.3Z" />
            </svg>
          </button>
        </Infobulle>
        <Infobulle texte="Abandonner" className="inline-flex">
          <button
            type="button"
            onClick={onAnnuler}
            aria-label="Abandonner"
            className="rounded p-0.5 text-encre-400 hover:bg-encre-100 hover:text-encre-800"
          >
            <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="currentColor" aria-hidden>
              <path d="m8 6.6 3.3-3.3 1.4 1.4L9.4 8l3.3 3.3-1.4 1.4L8 9.4l-3.3 3.3-1.4-1.4L6.6 8 3.3 4.7l1.4-1.4L8 6.6Z" />
            </svg>
          </button>
        </Infobulle>
      </div>

      <div className="flex flex-wrap justify-center gap-1 pr-10">
        {dates.map((d) => {
          const retenu = selection.has(d);
          const hors = periodeDe(d);
          return (
            <Infobulle key={d} texte={hors ? hors.libelle : null} className="inline-flex">
              <button
                type="button"
                onClick={() => onBasculer(d)}
                className={`tabulaire rounded border px-1.5 py-0.5 text-xs transition ${
                  retenu
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : hors
                      ? 'border-encre-300 bg-encre-200 text-encre-500'
                      : 'border-encre-300 bg-white text-encre-700 hover:border-blue-400'
                }`}
              >
                {jourMois(d)}
              </button>
            </Infobulle>
          );
        })}
      </div>
    </>
  );
}

function BlocPeriodes({
  periodes,
  modifiable,
  onAjouter,
  onRetirer,
}: {
  periodes: Periode[];
  modifiable: boolean;
  onAjouter: () => void;
  onRetirer: (id: string) => Promise<void>;
}) {
  const vacances = periodes.filter((p) => p.type === 'vacances');
  const feries = periodes.filter((p) => p.type === 'ferie');

  return (
    <div className="carte mt-6 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-encre-500">
          Vacances et jours fériés
        </h2>
        {modifiable && (
          <button type="button" className="bouton-secondaire py-1.5" onClick={onAjouter}>
            Ajouter une période
          </button>
        )}
      </div>

      <p className="mb-3 text-xs text-encre-400">
        Elles n’engendrent pas le calendrier, elles l’éclairent : les dates concernées sont grisées
        à la saisie, sans être interdites. Sur 2025-26, quatre séances manquent sans qu’aucune
        période les explique — le samedi avant Pâques et le pont de l’Ascension.
      </p>

      {periodes.length === 0 ? (
        <p className="text-sm text-encre-400">Aucune période enregistrée.</p>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <div className="etiquette">Vacances scolaires ({vacances.length})</div>
            <ul className="mt-1 space-y-1.5">
              {vacances.map((p) => (
                <li key={p.id} className="flex items-center gap-2 text-sm">
                  <span className="min-w-0 flex-1">{p.libelle}</span>
                  <span className="tabulaire text-xs text-encre-500">
                    {formaterDate(p.date_debut)} → {formaterDate(p.date_fin)}
                  </span>
                  {modifiable && (
                    <button
                      type="button"
                      className="text-xs text-red-600 underline"
                      onClick={() => void onRetirer(p.id)}
                    >
                      Retirer
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="etiquette">Jours fériés ({feries.length})</div>
            <ul className="mt-1 space-y-1.5">
              {feries.map((p) => (
                <li key={p.id} className="flex items-center gap-2 text-sm">
                  <span className="min-w-0 flex-1">{p.libelle}</span>
                  <span className="tabulaire text-xs text-encre-500">
                    {formaterDate(p.date_debut)}
                  </span>
                  {modifiable && (
                    <button
                      type="button"
                      className="text-xs text-red-600 underline"
                      onClick={() => void onRetirer(p.id)}
                    >
                      Retirer
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

function BlocEvenements({
  evenements,
  modifiable,
  onAjouter,
  onRetirer,
}: {
  evenements: Seance[];
  modifiable: boolean;
  onAjouter: () => void;
  onRetirer: (id: string) => Promise<void>;
}) {
  return (
    <div className="carte mt-6 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-encre-500">
          Événements de l’année
        </h2>
        {modifiable && (
          <button type="button" className="bouton-secondaire py-1.5" onClick={onAjouter}>
            Ajouter un événement
          </button>
        )}
      </div>

      {evenements.length === 0 ? (
        <p className="text-sm text-encre-400">Aucun événement enregistré.</p>
      ) : (
        <ul className="space-y-2">
          {evenements
            .slice()
            .sort((a, b) => a.date_seance.localeCompare(b.date_seance))
            .map((e) => (
              <li key={e.id} className="flex flex-wrap items-center gap-3 text-sm">
                <span className="tabulaire w-28 text-encre-500">{formaterDate(e.date_seance)}</span>
                <span className="font-medium">{e.libelle}</span>
                {e.lieu && <span className="text-xs text-encre-500">{e.lieu}</span>}
                <Badge ton="alerte">
                  {JOURS.find((j) => j.code === jourDe(e.date_seance))?.libelle}
                </Badge>
                {modifiable && (
                  <button
                    type="button"
                    className="ml-auto text-xs text-red-600 underline"
                    onClick={() => void onRetirer(e.id)}
                  >
                    Retirer
                  </button>
                )}
              </li>
            ))}
        </ul>
      )}
      <p className="mt-3 text-xs text-encre-400">
        Un événement ne compte pas comme séance : il occupe la journée et explique l’absence de
        cours ce jour-là, pour le seul jour de la semaine concerné.
      </p>
    </div>
  );
}

/**
 * Proposition de dates entre deux bornes — une AMORCE, pas le calendrier.
 * Le vrai s'en écarte : vacances, jours fériés, auditions, ponts.
 */
function FormulaireGeneration({
  ouverte,
  anneeId,
  debutDefaut,
  finDefaut,
  onFermer,
  onGenere,
}: {
  ouverte: boolean;
  anneeId: string;
  debutDefaut: string;
  finDefaut: string;
  onFermer: () => void;
  onGenere: (dates: string[]) => void;
}) {
  const [debut, setDebut] = useState(debutDefaut);
  const [fin, setFin] = useState(finDefaut);
  const [jours, setJours] = useState<number[]>([1, 2, 3, 4, 5, 6]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const { dates } = await api.post<{ dates: string[] }>(
        `/api/annees/${anneeId}/seances/proposition`,
        { debut, fin, jours },
      );
      onGenere(dates);
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Génération impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre="Proposer des dates" ouverte={ouverte} onFermer={onFermer}>
      <form onSubmit={soumettre} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Champ libelle="Première semaine">
            <input
              className="champ"
              type="date"
              value={debut}
              onChange={(e) => setDebut(e.target.value)}
              required
            />
          </Champ>
          <Champ libelle="Dernière semaine">
            <input
              className="champ"
              type="date"
              value={fin}
              onChange={(e) => setFin(e.target.value)}
              required
            />
          </Champ>
        </div>

        <div>
          <span className="etiquette">Jours concernés</span>
          <div className="mt-1 flex flex-wrap gap-3">
            {JOURS.map((j) => (
              <label key={j.code} className="flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={jours.includes(j.code)}
                  onChange={(e) =>
                    setJours(
                      e.target.checked
                        ? [...jours, j.code]
                        : jours.filter((autre) => autre !== j.code),
                    )
                  }
                />
                {j.libelle}
              </label>
            ))}
          </div>
        </div>

        <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-500">
          La proposition retient <strong>toutes</strong> les dates de ces jours entre les deux
          bornes, vacances comprises. Elle amorce la saisie : ajustez ensuite cellule par cellule,
          puis enregistrez.
        </p>

        {erreur && (
          <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {erreur}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className="bouton-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button
            type="submit"
            className="bouton-principal"
            disabled={enCours || jours.length === 0}
          >
            Proposer
          </button>
        </div>
      </form>
    </Modale>
  );
}

function FormulaireEvenement({
  ouverte,
  anneeId,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  anneeId: string;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [valeurs, setValeurs] = useState({ dateSeance: '', libelle: '', lieu: '' });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await api.post<Seance>(`/api/annees/${anneeId}/seances`, {
        dateSeance: valeurs.dateSeance,
        type: 'evenement',
        libelle: valeurs.libelle,
        lieu: valeurs.lieu || null,
      });
      setValeurs({ dateSeance: '', libelle: '', lieu: '' });
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre="Ajouter un événement" ouverte={ouverte} onFermer={onFermer}>
      <form onSubmit={soumettre} className="space-y-4">
        <Champ libelle="Date">
          <input
            className="champ"
            type="date"
            value={valeurs.dateSeance}
            onChange={(e) => setValeurs({ ...valeurs, dateSeance: e.target.value })}
            required
            autoFocus
          />
        </Champ>
        <Champ libelle="Intitulé" aide="« Auditions de Noël », « Portes ouvertes instruments »…">
          <input
            className="champ"
            value={valeurs.libelle}
            onChange={(e) => setValeurs({ ...valeurs, libelle: e.target.value })}
            required
          />
        </Champ>
        <Champ libelle="Lieu">
          <input
            className="champ"
            value={valeurs.lieu}
            onChange={(e) => setValeurs({ ...valeurs, lieu: e.target.value })}
          />
        </Champ>

        <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-500">
          Un événement ne compte pas comme séance. Il n’enlève pas non plus le cours du jour : si
          les cours n’ont pas lieu, retirez la date de la grille.
        </p>

        {erreur && (
          <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {erreur}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className="bouton-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button type="submit" className="bouton-principal" disabled={enCours}>
            Ajouter
          </button>
        </div>
      </form>
    </Modale>
  );
}

function FormulairePeriode({
  ouverte,
  anneeId,
  onFermer,
  onEnregistre,
}: {
  ouverte: boolean;
  anneeId: string;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [valeurs, setValeurs] = useState({
    type: 'vacances' as TypePeriode,
    libelle: '',
    dateDebut: '',
    dateFin: '',
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await api.post<Periode>(`/api/annees/${anneeId}/periodes`, {
        type: valeurs.type,
        libelle: valeurs.libelle,
        dateDebut: valeurs.dateDebut,
        // Un férié tient sur un jour : le serveur ramène la fin au début.
        dateFin: valeurs.type === 'ferie' ? null : valeurs.dateFin || null,
      });
      setValeurs({ type: 'vacances', libelle: '', dateDebut: '', dateFin: '' });
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre="Ajouter une période" ouverte={ouverte} onFermer={onFermer}>
      <form onSubmit={soumettre} className="space-y-4">
        <Champ libelle="Type">
          <select
            className="champ"
            value={valeurs.type}
            onChange={(e) => setValeurs({ ...valeurs, type: e.target.value as TypePeriode })}
          >
            <option value="vacances">Vacances scolaires</option>
            <option value="ferie">Jour férié</option>
          </select>
        </Champ>

        <Champ
          libelle="Intitulé"
          aide={
            valeurs.type === 'ferie'
              ? '« Lundi de Pentecôte », « Armistice 1918 »…'
              : '« Vacances de la Toussaint », « Vacances d’hiver »…'
          }
        >
          <input
            className="champ"
            value={valeurs.libelle}
            onChange={(e) => setValeurs({ ...valeurs, libelle: e.target.value })}
            required
          />
        </Champ>

        <div className="grid grid-cols-2 gap-3">
          <Champ libelle={valeurs.type === 'ferie' ? 'Date' : 'Premier jour'}>
            <input
              className="champ"
              type="date"
              value={valeurs.dateDebut}
              onChange={(e) => setValeurs({ ...valeurs, dateDebut: e.target.value })}
              required
            />
          </Champ>
          {valeurs.type === 'vacances' && (
            <Champ libelle="Dernier jour" aide="La veille de la reprise des cours.">
              <input
                className="champ"
                type="date"
                value={valeurs.dateFin}
                onChange={(e) => setValeurs({ ...valeurs, dateFin: e.target.value })}
                required
              />
            </Champ>
          )}
        </div>

        <p className="rounded-md border border-encre-200 bg-encre-50 p-3 text-xs text-encre-500">
          Le calendrier officiel donne « fin des cours » et « reprise des cours » : la période court
          du <strong>lendemain</strong> de la fin à la <strong>veille</strong> de la reprise.
        </p>

        {erreur && (
          <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {erreur}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" className="bouton-secondaire" onClick={onFermer}>
            Annuler
          </button>
          <button type="submit" className="bouton-principal" disabled={enCours}>
            Ajouter
          </button>
        </div>
      </form>
    </Modale>
  );
}
